<main class="flex flex-column align-items-center">
  <div
    id="terms-panel"
    class="flex flex-column w-11 lg:w-8 xl:w-6 justify-content-center align-content-center"
  >
    <!-- Navigation Tabs -->
    <nav class="upload-nav">
      <button
        [class.active]="currentPage === 'info'"
        (click)="switchPage('info')"
      >
        Upload Info
      </button>
      <button
        [class.active]="currentPage === 'netcdf'"
        (click)="switchPage('netcdf')"
      >
        NetCDF Files
      </button>
      <button
        [class.active]="currentPage === 'csv'"
        (click)="switchPage('csv')"
      >
        CSV Files
      </button>
    </nav>

    <!-- Pages -->
    <!---------- INFO section ---------->
    @if (currentPage === "info") {
      <div>
        <h1>File Upload Info</h1>
        <p class="text-primary">
          This new, experimantal feature allows users to upload their own
          datasets for comparison and validation against the reference products
          available in QA4SM:
        </p>
        <ul class="dataset-links">
          <li>
            <a routerLink="/datasets" target="_blank" rel="noopener noreferrer"
              >Satellite datasets</a
            >
          </li>
          <li>
            <a
              href="https://cds.climate.copernicus.eu/datasets"
              target="_blank"
              rel="noopener noreferrer"
              >ERA model data</a
            >
          </li>
          <li>
            <a
              href="https://ismn.earth/en/"
              target="_blank"
              rel="noopener noreferrer"
              >ISMN monitoring stations</a
            >
          </li>
        </ul>
        <h2>Important Notes</h2>
        <ul>
          <li>
            Your uploaded data is <strong>private</strong> – no other users can
            see, access, or download it.
          </li>
          <li>
            Uploads can be either <strong>.zip</strong> files containing
            <strong>CSV (.csv)</strong> files or
            <strong>NetCDF (.nc/.nc4)</strong> files.
          </li>
          <li>
            If you plan to upload datasets larger than several GB, please
            contact us first at
            <a href="mailto:support@qa4sm.eu">support&#64;qa4sm.eu</a>. We’ll
            help ensure your data is handled efficiently and accessible after
            upload.
          </li>
          <li>
            Validation results with uploaded data cannot be published due to
            limited traceability.
          </li>
          <li>
            Data persistence is not yet guaranteed. Future updates to QA4SM may
            require you to re-upload your files.
          </li>
        </ul>
        <h2>Troubleshooting</h2>
        <p class="text-primary">
          If your upload fails or data cannot be processed correctly, please
          check these common issues:
        </p>
        <ul>
          <li>
            <strong>Invalid file type:</strong> The ZIP archive should contain
            only <code>.csv</code> files and optionally a
            <code>metadata.yml</code> file, OR the uploaded file should be a
            <code>.nc</code> or <code>.nc4</code> file (not zipped).
          </li>
          <li>
            <strong>Incorrect ZIP structure:</strong> Avoid nested folders or
            unrelated files.
          </li>
          <li>
            <strong>Missing variables:</strong> Ensure your files include
            <strong>latitude, longitude, and time</strong> information.
          </li>
          <li>
            <strong>Format errors:</strong> See examples in
            <button class="link-btn" (click)="switchPage('netcdf')">
              NetCDF
            </button>
            or
            <button class="link-btn" (click)="switchPage('csv')">CSV</button>.
          </li>
          <li>
            <strong>File too large:</strong> Try a smaller dataset or contact
            support.
          </li>
        </ul>
        <p class="text-primary">
          <strong>You need help?</strong> Don’t hesitate to reach out at
          <a href="mailto:support@qa4sm.eu">support&#64;qa4sm.eu</a>. We are
          happy to assist you!
        </p>
      </div>
    }

    <!---------- NetCDF section ---------->

    @if (currentPage === "netcdf") {
      <div>
        <h1>Upload NetCDF4 Image Stacks</h1>
        <p class="text-primary">
          Many Level 3 and Level 4 soil moisture datasets are provided in image
          format with three data dimensions:
          <strong>latitude</strong>, <strong>longitude</strong>, and
          <strong>time</strong>. Multiple images can be combined along the time
          axis to form a <strong>data cube</strong>. From these cubes, QA4SM
          extracts time series data for validation.
        </p>
        <h2>General Requirements</h2>
        <ul>
          <li>
            Only <strong>NetCDF4 format</strong> is supported (<code>.nc</code>
            or <code>.nc4</code> files).
          </li>
          <li>
            Files must contain exactly <strong>three dimensions</strong>:
            latitude, longitude, and time.
          </li>
          <li>
            Only numeric data types are supported (<code>integer</code>,
            <code>float</code>, <code>double</code>).
          </li>
          <li>
            Variable names, dimensions, and attributes:
            <ul>
              <li>
                Should use only
                <strong>letters, digits, and underscores</strong>.
              </li>
              <li>Should not exceed <strong>30 characters</strong>.</li>
            </ul>
          </li>
          <li>
            At least <strong>one soil moisture variable</strong> must be
            included. (If multiple are present, you will select one after
            upload.)
          </li>
        </ul>
        <h2>Latitude and Longitude</h2>
        <ul>
          <li>
            Coordinates must follow the
            <strong>WGS84 reference system</strong> (Longitude: -180° to 180°,
            Latitude: -90° to 90°).
          </li>
          <li>
            Accepted <strong>units</strong>:
            <ul>
              <li>
                Longitude: <code>degrees_east</code> (or similar variations)
              </li>
              <li>
                Latitude: <code>degrees_north</code> (or similar variations)
              </li>
            </ul>
          </li>
          <li>
            Two formats are supported:
            <ul>
              <li>
                <strong>1D coordinates</strong> – Separate arrays for lat and
                lon.
              </li>
              <li>
                <strong>2D coordinates</strong> – Arrays with values for every
                grid point.
              </li>
            </ul>
          </li>
        </ul>
        <h2>Observation Time</h2>
        <ul>
          <li>
            Time must be a <strong>1D coordinate variable</strong> with the
            attribute:
            <pre><code>"&lt;unit&gt; since &lt;reference date&gt;"</code></pre>
            Example: <code>days since 2000-01-01 00:00:00</code>
          </li>
          <li>Recommended attribute: <code>standard_name: time</code></li>
          <li>
            A calendar attribute (e.g. proleptic_gregorian) is recommended but
            optional.
          </li>
          <li>If timestamps cannot be globally applied:</li>
          <ul>
            <li>
              A separate <strong>3D time offset</strong> variable (same shape as
              the data variable) must be provided.
            </li>
            <li>
              The time offset should be given in seconds relative to the base
              <code>time</code> value
            </li>
          </ul>
        </ul>
        <h2>Naming & Attributes</h2>
        <ul>
          <li>Provide clear, unique variable names.</li>
          <li>Include <strong>units</strong> for all variables.</li>
          <li>
            Define <code>valid_range</code>, <code>valid_min</code>, or
            <code>valid_max</code> where possible.
          </li>
          <li>
            Optional compression attributes: <code>add_offset</code>,
            <code>scale_factor</code>, <code>zlib</code>.
          </li>
        </ul>
        <h2>Tip</h2>
        <p class="text-primary">
          Before uploading, test your file locally using Python's
          <a
            href="https://docs.xarray.dev/"
            target="_blank"
            rel="noopener noreferrer"
            >xarray</a
          >:
        </p>
        <textarea
          id="copy-code"
          class="sr-only"
          aria-hidden="true"
          style="position: absolute; left: -9999px"
        >
  import xarray as xr
  ds = xr.open_dataset("myDataset.nc")
  print(ds)
</textarea
        >
        <pre><code>import xarray as xr
  ds = xr.open_dataset("myDataset.nc")
print(ds)</code></pre>
        <!-- Copy button -->
        <a class="copy-btn" (click)="copy('copy-code')">
          <i class="pi pi-fw pi-copy"></i> Copy
        </a>
        <p class="text-primary">
          This will ensure your NetCDF file is correctly structured and
          compatible with QA4SM.
        </p>
        <p class="text-primary">
          Below is an example of how user NetCDF dataset might look when opened
          with
          <a
            href="https://docs.xarray.dev/"
            target="_blank"
            rel="noopener noreferrer"
            >xarray</a
          >:
        </p>
        <p class="text-primary"><strong>NetCDF file example:</strong></p>
        <pre><code>&lt;xarray.Dataset&gt; Size: 58kB
  Dimensions:               (locations: 100, time: 10)
  Coordinates:
  longitude             (locations) float32 4kB ...
  latitude              (locations) float32 4kB ...
  elevation             (locations) float32 4kB ...
  * time                  (time) datetime64[ns] 80B 2023-01-01 ... 2023-01-10
  Dimensions without coordinates: locations
  Data variables:
  station_id            (locations) int64 7kB ...
  station_name          (locations) &lt;U20 4kB ...
  soil_moisture         (locations, time) float32 36kB ...
  Attributes:
  id:                         USER-SOILMOISTURE-UPLOAD-2023010100...
  date_created:               20230815T102300Z
  featureType:                timeSeries
  title:                      User Soil Moisture Dataset for Validation
  institution:                User Research Lab, User University
  contact:                    my.email&#64;email.com
  data_owner:                 User Name
  geospatial_vertical_max:    1.0
  geospatial_lat_units:       degrees_north
  geospatial_lon_units:       degrees_east
  geospatial_lat_resolution:  0.1 degree
  geospatial_lon_resolution:  0.1 degree
  spatial_resolution:         10km
</code></pre>
        <h2>Example Files</h2>
        <p class="text-primary">
          You can download example files from our
          <a
            href="https://github.com/awst-austria/qa4sm-testdata/tree/master/user_data"
            target="_blank"
            rel="noopener noreferrer"
            >GitHub repository</a
          >.
        </p>
      </div>
    }

    <!---------- CSV section ---------->
    @if (currentPage === "csv") {
      <div>
        <h1>Upload Time Series (CSV Format)</h1>
        <p class="text-primary">
          This option allows you to upload individual time series files in
          <strong>CSV format</strong>, bundled into a single
          <strong>ZIP archive</strong>. It is particularly useful for datasets
          with <strong>irregular timestamps</strong>
          or when uploading data only for specific in-situ locations to avoid
          large datasets.
        </p>
        <h2>File Name Convention</h2>
        <p class="text-primary">
          Each CSV file inside the ZIP must be named as:
        </p>
        <pre><code>&lt;DATASET_NAME&gt;_gpi=&lt;GPI&gt;_lat=&lt;LAT&gt;_lon=&lt;LON&gt;.csv</code></pre>
        <ul class="no-bullets">
          <li>
            <strong>&lt;DATASET_NAME&gt;</strong> → Dataset name
            <span class="highlight">(same for all files)</span>
          </li>
          <li>
            <strong>&lt;GPI&gt;</strong> → Unique grid point index for the
            location
          </li>
          <li>
            <strong>&lt;LAT&gt;</strong> → Latitude in degrees North (-90 to 90)
          </li>
          <li>
            <strong>&lt;LON&gt;</strong> → Longitude in degrees East (-180 to
            180)
          </li>
        </ul>
        <p class="text-primary"><strong>CSV file name example:</strong></p>
        <pre><code>myDataset_gpi=000001_lat=38.887_lon=-101.043.csv</code></pre>
        <h2>File Format Requirements</h2>
        <ul>
          <li>
            File must include a <strong>header row</strong> with column names
            <span class="highlight">(same for all files)</span>.
          </li>
          <li>
            The <strong>first column</strong> must contain timestamps (format
            parseable by
            <a
              href="https://dateutil.readthedocs.io/en/stable/parser.html"
              target="_blank"
              rel="noopener noreferrer"
              >Python dateutil</a
            >).
          </li>
          <li>
            File can include one or more <strong>data columns</strong> e.g.,
            soil moisture values
            <span class="highlight">(same number of columns for all files)</span
            >.
          </li>
        </ul>
        <p class="text-primary"><strong>CSV file example:</strong></p>
        <pre><code>Time,SM
    2016-06-01 01:30:07,0.18278
    2016-06-02 00:51:39,0.2003
    2016-06-02 12:08:07,0.181
  2016-06-04 01:13:32,0.22323</code></pre>
        <h2>Tip</h2>
        <p class="text-primary">
          Verify your CSV files locally before uploading using
          <a
            href="https://pandas.pydata.org/"
            target="_blank"
            rel="noopener noreferrer"
            >pandas</a
          >:
        </p>
        <textarea
          id="copy-code"
          class="sr-only"
          aria-hidden="true"
          style="position: absolute; left: -9999px"
        >
    import pandas as pd
    df = pd.read_csv("myDataset_gpi=000001_lat=38.887_lon=-101.043.csv",
    index_col=0, parse_dates=True)
  </textarea
        >
        <pre><code>import pandas as pd
    df = pd.read_csv("mydataset_gpi=0_lat=43.01_lon=2.47.csv",
    index_col=0, parse_dates=True)
  print(df)</code></pre>
        <!-- Copy button -->
        <a class="copy-btn" (click)="copy('copy-code')">
          <i class="pi pi-fw pi-copy"></i> Copy
        </a>
        <h2>Metadata</h2>
        <p class="text-primary">
          Metadata for the data variables (column-names in the CSV files) e.g.
          units, long name, should be provided in an additional file with the
          name <strong>metadata.yml</strong>. This should have a similar format
          to this example.
        </p>
        <p class="text-primary"><strong>Metadata file example:</strong></p>
        <pre><code>SM:
    long_name: soil moisture
    units: m^3/m^3
    ⋮
    myvarnameX:
    long_name: soil moisture x
  units: m^3/m^3</code></pre>
        <p class="text-primary">
          All CSV files and optionally the metadata file have to be in the same
          directory. This directory has to be uploaded as zip file.
        </p>
        <h2>Example Files</h2>
        <p class="text-primary">
          You can download example files from our
          <a
            href="https://github.com/awst-austria/qa4sm-testdata/tree/master/user_data"
            target="_blank"
            rel="noopener noreferrer"
            >GitHub repository</a
          >.
        </p>
      </div>
    }
  </div>
</main>
